import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { apiClient } from '../../packages/api-client';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Badge, Dialog, PageHeader, StateView, Table } from '../../packages/ui';
import type { Column } from '../../packages/ui';
import './platform.css';

// A type alias (not an interface) so it satisfies Table's Record<string, unknown> row bound.
type Tenant = {
    id: string;
    name: string;
    slug: string;
    plan: string;
    is_active: boolean;
    created_at: string;
    updated_at?: string;
    deleted_at?: string | null;
};

/** tenants.GraceDays (backend/internal/tenants/model.go). */
const GRACE_DAYS = 30;
const PLANS = ['free', 'pro', 'enterprise'];
const STATUSES = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'scheduled', label: 'Scheduled for deletion' },
] as const;

function purgeDate(deletedAt: string): string {
    const d = new Date(deletedAt);
    d.setDate(d.getDate() + GRACE_DAYS);
    return d.toLocaleDateString();
}

function statusOf(t: Tenant): 'scheduled' | 'active' | 'inactive' {
    if (t.deleted_at) return 'scheduled';
    return t.is_active ? 'active' : 'inactive';
}

function StatusBadge({ t }: { t: Tenant }) {
    const s = statusOf(t);
    if (s === 'scheduled') return <Badge variant="warning">Deletes {purgeDate(t.deleted_at as string)}</Badge>;
    return s === 'active' ? <Badge variant="success">Active</Badge> : <Badge variant="danger">Inactive</Badge>;
}

function errMessage(err: unknown, fallback: string): string {
    return (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;
}

/**
 * Platform › Tenants: the registry (search/filter, create, orphan-data
 * diagnostics) and, at /admin/tenants/:id, the selected tenant's detail with
 * its supported edits and guarded lifecycle actions.
 */
export function TenantsPage() {
    usePageTitle('Tenants');
    const { id } = useParams();
    const { search } = useLocation();
    const [params, setParams] = useSearchParams();
    const q = params.get('q') ?? '';
    const status = params.get('status') ?? 'all';

    const [tenants, setTenants] = useState<Tenant[] | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showOrphans, setShowOrphans] = useState(false);

    const load = useCallback(() => {
        setLoadError(false);
        apiClient.get<{ data: Tenant[] }>('/tenants')
            .then((r) => setTenants(r.data.data ?? []))
            .catch(() => { setTenants(null); setLoadError(true); });
    }, []);
    useEffect(() => { load(); }, [load]);

    const setParam = (key: string, value: string, empty: string) => {
        const next = new URLSearchParams(params);
        if (value === empty) next.delete(key); else next.set(key, value);
        setParams(next, { replace: true });
    };

    const needle = q.trim().toLowerCase();
    const shown = (tenants ?? []).filter((t) =>
        (status === 'all' || statusOf(t) === status)
        && (!needle || t.name.toLowerCase().includes(needle) || t.slug.toLowerCase().includes(needle)));

    const columns: Column<Tenant>[] = [
        {
            key: 'name', header: 'Name', rowHeader: true,
            render: (t) => (
                <Link to={{ pathname: `/admin/tenants/${t.id}`, search }} className="platform-row-link" aria-current={t.id === id ? 'true' : undefined}>
                    {t.name}
                </Link>
            ),
        },
        { key: 'slug', header: 'Slug', render: (t) => <code>{t.slug}</code> },
        { key: 'plan', header: 'Plan', render: (t) => <Badge variant={t.plan === 'free' ? 'neutral' : 'info'}>{t.plan}</Badge> },
        { key: 'status', header: 'Status', render: (t) => <StatusBadge t={t} /> },
        { key: 'created_at', header: 'Created', render: (t) => new Date(t.created_at).toLocaleDateString() },
    ];

    let body: React.ReactNode;
    if (loadError) body = <StateView state="error" message="The tenant registry request failed." onRetry={load} />;
    else if (!tenants) body = <StateView state="loading" label="Loading tenants" />;
    else if (tenants.length === 0) body = <StateView state="empty" title="No tenants yet" message="Create the first tenant to start." />;
    else if (shown.length === 0) body = <StateView state="filtered-empty" onClearFilters={() => setParams(new URLSearchParams(), { replace: true })} />;
    else body = <Table columns={columns} data={shown} caption="Tenants" hideCaption rowKey={(t) => t.id} />;

    return (
        <div>
            <PageHeader
                breadcrumb={[{ label: 'Platform console' }, { label: 'Tenants' }]}
                title="Tenants"
                subtitle={tenants ? `${tenants.length} tenant${tenants.length === 1 ? '' : 's'} in the registry, including any scheduled for deletion.` : undefined}
                actions={<>
                    <button type="button" className="btn btn-secondary btn-md" onClick={() => setShowOrphans(true)}>
                        <Trash2 size={16} aria-hidden="true" /> Orphaned data diagnostics
                    </button>
                    <button type="button" className="btn btn-primary btn-md" onClick={() => setShowCreate(true)}>+ New tenant</button>
                </>}
            />

            <div className="platform-toolbar">
                <div className="form-group">
                    <label className="form-label" htmlFor="tenant-search">Search</label>
                    <input id="tenant-search" type="search" className="form-input" aria-label="Search tenants"
                        placeholder="Name or slug" value={q} onChange={(e) => setParam('q', e.target.value, '')} />
                </div>
                <div className="form-group">
                    <label className="form-label" htmlFor="tenant-status">Status</label>
                    <select id="tenant-status" className="form-input" value={status} onChange={(e) => setParam('status', e.target.value, 'all')}>
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
            </div>

            <div className={`platform-split${id ? ' platform-split--detail' : ''}`}>
                <section aria-label="Tenant registry" style={{ minWidth: 0 }}>{body}</section>
                {id && (
                    <section className="platform-detail" aria-label="Tenant detail">
                        {/* key: a new tenant remounts the detail, so no state of the previous one survives. */}
                        <TenantDetail key={id} id={id} onChanged={load} />
                    </section>
                )}
            </div>

            <CreateTenantDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
            {showOrphans && <OrphanDialog onClose={() => setShowOrphans(false)} onPurged={load} />}
        </div>
    );
}

// ─── Detail ──────────────────────────────────────────────────────────────────

type DetailState = { kind: 'loading' } | { kind: 'error' } | { kind: 'ok'; tenant: Tenant };
type Confirm = null | 'soft' | 'hard';

function TenantDetail({ id, onChanged }: { id: string; onChanged: () => void }) {
    const navigate = useNavigate();
    const { search } = useLocation();
    const [state, setState] = useState<DetailState>({ kind: 'loading' });
    const [form, setForm] = useState({ name: '', plan: 'free', is_active: true });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<Confirm>(null);

    const show = (t: Tenant) => {
        setState({ kind: 'ok', tenant: t });
        setForm({ name: t.name, plan: t.plan, is_active: t.is_active });
    };

    const fetchTenant = useCallback((isCurrent: () => boolean = () => true) => {
        apiClient.get<Tenant>(`/tenants/${id}`)
            .then((r) => { if (isCurrent()) show(r.data); })
            .catch(() => { if (isCurrent()) setState({ kind: 'error' }); });
    }, [id]);

    useEffect(() => {
        let current = true;
        fetchTenant(() => current);
        return () => { current = false; };
    }, [fetchTenant]);

    if (state.kind === 'loading') return <StateView state="loading" label="Loading tenant" />;
    if (state.kind === 'error') {
        return <StateView state="error" title="Tenant couldn't be loaded" message="It may not exist, or the request failed." onRetry={() => { setState({ kind: 'loading' }); fetchTenant(); }} />;
    }
    const t = state.tenant;

    async function save(ev: React.FormEvent) {
        ev.preventDefault();
        setSaving(true); setSaveError(null);
        try {
            // Mirrors UpdateTenantInput (backend/internal/tenants/model.go): no slug.
            const { data } = await apiClient.put<Tenant>(`/tenants/${t.id}`, form);
            show(data);
            onChanged();
        } catch (err) {
            setSaveError(errMessage(err, 'Failed to update tenant.'));
        } finally {
            setSaving(false);
        }
    }

    async function restore() {
        setActionError(null);
        try {
            await apiClient.post(`/tenants/${t.id}/restore`);
            fetchTenant();
            onChanged();
        } catch (err) {
            setActionError(errMessage(err, 'Failed to restore tenant.'));
        }
    }

    return (
        <>
            <div>
                <h2>{t.name}</h2>
                <div style={{ marginTop: 'var(--space-2)' }}><StatusBadge t={t} /></div>
            </div>

            <dl className="platform-facts">
                <dt>Tenant ID</dt><dd><code>{t.id}</code></dd>
                <dt>Created</dt><dd>{new Date(t.created_at).toLocaleString()}</dd>
                {t.updated_at && <><dt>Updated</dt><dd>{new Date(t.updated_at).toLocaleString()}</dd></>}
            </dl>
            <p className="platform-hint">
                Billing and AI usage for this tenant: <Link to="/admin/billing/tenants">Billing &amp; AI usage</Link>.
            </p>

            {t.deleted_at ? (
                <div className="platform-danger-zone">
                    <h3>Recovery</h3>
                    <p style={{ fontSize: 'var(--font-size-sm)' }}>
                        Scheduled for permanent deletion on {purgeDate(t.deleted_at)}. The tenant is locked until then;
                        restoring it cancels the deletion.
                    </p>
                    {actionError && <p role="alert" className="platform-error">{actionError}</p>}
                    <div className="platform-actions">
                        <button type="button" className="btn btn-primary btn-sm" onClick={restore}>Restore tenant</button>
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => setConfirm('hard')}>Delete permanently now…</button>
                    </div>
                </div>
            ) : (
                <>
                    <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        <h3>Details</h3>
                        {saveError && <p role="alert" className="platform-error">{saveError}</p>}
                        <div className="form-group">
                            <label className="form-label" htmlFor="tenant-name">Organization name *</label>
                            <input id="tenant-name" className="form-input" required value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                        </div>
                        <div className="form-group">
                            <label className="form-label" htmlFor="tenant-slug">Slug</label>
                            <input id="tenant-slug" className="form-input" value={t.slug} readOnly aria-describedby="tenant-slug-help" />
                            <span id="tenant-slug-help" className="platform-hint">Set when the tenant is created; it cannot be changed after creation.</span>
                        </div>
                        <div className="form-group">
                            <label className="form-label" htmlFor="tenant-plan">Plan</label>
                            <select id="tenant-plan" className="form-input" value={form.plan} onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}>
                                {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                            <span className="platform-hint">Changes the stored plan only; it does not create or change a Stripe subscription.</span>
                        </div>
                        <div className="form-group">
                            <label className="form-label" htmlFor="tenant-active">Access</label>
                            <select id="tenant-active" className="form-input" value={form.is_active ? 'active' : 'inactive'}
                                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === 'active' }))}>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div className="platform-actions">
                            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
                        </div>
                    </form>
                    <div className="platform-danger-zone">
                        <h3>Deletion</h3>
                        <p className="platform-hint">Scheduling deletion locks the tenant now and purges it after {GRACE_DAYS} days unless restored.</p>
                        <div className="platform-actions">
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => setConfirm('soft')}>Schedule deletion…</button>
                        </div>
                    </div>
                </>
            )}

            {confirm === 'soft' && (
                <SoftDeleteDialog tenant={t} onClose={() => setConfirm(null)} onDone={() => { setConfirm(null); fetchTenant(); onChanged(); }} />
            )}
            {confirm === 'hard' && (
                <PurgeDialog tenant={t} onClose={() => setConfirm(null)}
                    onDone={() => { onChanged(); navigate({ pathname: '/admin/tenants', search }); }} />
            )}
        </>
    );
}

/** Runs `action`, keeping the dialog open with the error if it fails. */
function useDialogAction(action: () => Promise<unknown>, onDone: () => void, failure: string) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const run = async () => {
        setBusy(true); setError(null);
        try { await action(); onDone(); } catch (err) { setError(errMessage(err, failure)); } finally { setBusy(false); }
    };
    return { busy, error, run };
}

function SoftDeleteDialog({ tenant, onClose, onDone }: { tenant: Tenant; onClose: () => void; onDone: () => void }) {
    const { busy, error, run } = useDialogAction(() => apiClient.delete(`/tenants/${tenant.id}`), onDone, 'Failed to schedule deletion.');
    return (
        <Dialog open onClose={onClose} title={`Schedule deletion of ${tenant.name}?`}
            footer={<>
                <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
                <button type="button" className="btn btn-danger btn-md" onClick={run} disabled={busy}>{busy ? 'Scheduling…' : 'Schedule deletion'}</button>
            </>}>
            {error && <p role="alert" className="platform-error">{error}</p>}
            <ul style={{ paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <li><strong>{tenant.name}</strong> (<code>{tenant.slug}</code>) is locked immediately.</li>
                <li>It and all of its data are permanently deleted in {GRACE_DAYS} days.</li>
                <li>You can restore it any time before then; superadmins are notified.</li>
            </ul>
        </Dialog>
    );
}

function PurgeDialog({ tenant, onClose, onDone }: { tenant: Tenant; onClose: () => void; onDone: () => void }) {
    const [typed, setTyped] = useState('');
    const { busy, error, run } = useDialogAction(() => apiClient.delete(`/tenants/${tenant.id}?hard=true`), onDone, 'Failed to delete tenant.');
    return (
        <Dialog open onClose={onClose} closeOnBackdrop={false} title={`Permanently delete ${tenant.name}?`}
            footer={<>
                <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
                <button type="button" className="btn btn-danger btn-md" onClick={run} disabled={busy || typed !== tenant.name}>
                    {busy ? 'Deleting…' : 'Delete permanently'}
                </button>
            </>}>
            {error && <p role="alert" className="platform-error">{error}</p>}
            <p>
                This deletes <strong>{tenant.name}</strong> and <strong>all of its data</strong> right now, skipping the rest of
                the {GRACE_DAYS}-day recovery window. This cannot be undone.
            </p>
            <div className="form-group" style={{ marginTop: 'var(--space-3)' }}>
                <label className="form-label" htmlFor="purge-confirm">Type the tenant name <strong>{tenant.name}</strong> to confirm</label>
                <input id="purge-confirm" className="form-input" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
            </div>
        </Dialog>
    );
}

// ─── Create ──────────────────────────────────────────────────────────────────

const slugify = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function CreateTenantDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
    const [form, setForm] = useState({ name: '', slug: '', plan: 'free' });
    const { busy, error, run } = useDialogAction(
        () => apiClient.post<Tenant>('/tenants', form),
        () => { setForm({ name: '', slug: '', plan: 'free' }); onCreated(); },
        'Failed to create tenant.',
    );
    return (
        <Dialog open={open} onClose={onClose} closeOnBackdrop={false} title="New tenant"
            footer={<>
                <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
                <button type="submit" form="create-tenant" className="btn btn-primary btn-md" disabled={busy}>{busy ? 'Creating…' : 'Create tenant'}</button>
            </>}>
            <form id="create-tenant" onSubmit={(e) => { e.preventDefault(); run(); }} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {error && <p role="alert" className="platform-error">{error}</p>}
                <div className="form-group">
                    <label className="form-label" htmlFor="new-tenant-name">Organization name *</label>
                    <input id="new-tenant-name" className="form-input" required autoFocus placeholder="Acme Corp" value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: slugify(e.target.value) }))} />
                </div>
                <div className="form-group">
                    <label className="form-label" htmlFor="new-tenant-slug">Slug</label>
                    <input id="new-tenant-slug" className="form-input" placeholder="acme-corp" value={form.slug} aria-describedby="new-tenant-slug-help"
                        onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
                    <span id="new-tenant-slug-help" className="platform-hint">Generated from the name. Must be unique; it cannot be changed later.</span>
                </div>
                <div className="form-group">
                    <label className="form-label" htmlFor="new-tenant-plan">Plan</label>
                    <select id="new-tenant-plan" className="form-input" value={form.plan} onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}>
                        {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
            </form>
        </Dialog>
    );
}

// ─── Orphan diagnostics ──────────────────────────────────────────────────────

type OrphanReport = { tenant_count: number; total_rows: number; row_counts: Record<string, number> };
type OrphanRow = { table: string; rows: number };

/**
 * Data stranded by tenants deleted before soft-delete shipped (rows whose
 * tenant no longer exists). The report is read-only; the purge is explicit.
 */
function OrphanDialog({ onClose, onPurged }: { onClose: () => void; onPurged: () => void }) {
    const [report, setReport] = useState<OrphanReport | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [purged, setPurged] = useState<number | null>(null);

    const load = useCallback(() => {
        setLoadError(false);
        apiClient.get<OrphanReport>('/tenants/orphans')
            .then((r) => setReport(r.data))
            .catch(() => setLoadError(true));
    }, []);
    useEffect(() => { load(); }, [load]);

    const { busy, error, run } = useDialogAction(
        async () => {
            const { data } = await apiClient.post<{ purged: number }>('/tenants/orphans/purge');
            setPurged(data.purged);
        },
        onPurged,
        'Failed to clean up orphaned data.',
    );

    const canPurge = report && report.tenant_count > 0 && purged === null;
    let body: React.ReactNode;
    if (purged !== null) body = <p role="status">Cleaned up {purged} orphaned tenant{purged === 1 ? '' : 's'}.</p>;
    else if (loadError) body = <StateView state="error" message="The orphan report request failed." onRetry={load} />;
    else if (!report) body = <StateView state="loading" label="Scanning for orphaned data" />;
    else if (report.tenant_count === 0) body = <p>No orphaned data found — nothing to clean up.</p>;
    else {
        const rows: OrphanRow[] = Object.entries(report.row_counts).sort(([a], [b]) => a.localeCompare(b)).map(([table, n]) => ({ table, rows: n }));
        body = (
            <>
                <p>
                    {report.tenant_count} deleted tenant{report.tenant_count === 1 ? '' : 's'} left data behind — {report.total_rows} rows in total.
                    These rows belong to tenant IDs that no longer exist. Superadmin accounts (no tenant) are never touched.
                </p>
                <Table<OrphanRow> caption="Orphaned rows by table" hideCaption density="compact" rowKey={(r) => r.table}
                    columns={[{ key: 'table', header: 'Table', rowHeader: true }, { key: 'rows', header: 'Rows' }]} data={rows} />
                <p className="platform-hint">Permanently deleting removes every row listed above. This cannot be undone.</p>
            </>
        );
    }

    return (
        <Dialog open onClose={onClose} title="Orphaned data diagnostics"
            footer={<>
                <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>{purged === null ? 'Cancel' : 'Close'}</button>
                {canPurge && (
                    <button type="button" className="btn btn-danger btn-md" onClick={run} disabled={busy}>
                        {busy ? 'Deleting…' : 'Permanently delete orphaned data'}
                    </button>
                )}
            </>}>
            {error && <p role="alert" className="platform-error">{error}</p>}
            {body}
        </Dialog>
    );
}
